/**
 * useFiatCap — plafond fiat administrable (P1-1, TRACK-2026-07-03-refactor-vague1).
 *
 * Le plafond n'est plus une constante front : il vit dans AppSettings (fiat_cap_usd),
 * est enforcé côté serveur sur toutes les initiations fiat, et est lu ici via le
 * endpoint public GET /data/fiat-cap. FIAT_CAP_USD (cartConstants) ne sert plus que
 * de fallback réseau.
 *
 * - fetchFiatCapUsd() : promesse mémoïsée module-level (1 fetch par session, TTL 60s)
 *   → utilisable dans les effets existants (useMultiVendorCapabilities).
 * - useFiatCap() : hook React → { fiatCapUsd } (fallback immédiat, mis à jour au fetch).
 */
import { useState, useEffect } from 'react';
import { fetchFiatCap } from '../services/DataService';
import { FIAT_CAP_USD } from '../utils/cart/cartConstants';

const TTL_MS = 60 * 1000;
let _cache = { value: null, at: 0, promise: null };

/**
 * fetchFiatCapUsd — renvoie le plafond USD (number). Jamais de throw : fallback FIAT_CAP_USD.
 * @returns {Promise<number>}
 */
export async function fetchFiatCapUsd() {
  const now = Date.now();
  if (_cache.value !== null && now - _cache.at < TTL_MS) return _cache.value;
  if (_cache.promise) return _cache.promise;
  _cache.promise = fetchFiatCap()
    .then((res) => {
      const v = parseFloat(res && res.fiatCapUsd);
      const cap = Number.isFinite(v) && v >= 0 ? v : FIAT_CAP_USD;
      _cache = { value: cap, at: Date.now(), promise: null };
      return cap;
    })
    .catch(() => {
      _cache = { value: null, at: 0, promise: null };
      return FIAT_CAP_USD; // fallback réseau — jamais bloquant
    });
  return _cache.promise;
}

/**
 * useFiatCap — hook React. Rend le fallback tout de suite, la valeur serveur dès le fetch.
 * @returns {{ fiatCapUsd: number }}
 */
export function useFiatCap() {
  const [fiatCapUsd, setFiatCapUsd] = useState(_cache.value !== null ? _cache.value : FIAT_CAP_USD);

  useEffect(() => {
    let cancelled = false;
    fetchFiatCapUsd().then((cap) => {
      if (!cancelled) setFiatCapUsd(cap);
    });
    return () => { cancelled = true; };
  }, []);

  return { fiatCapUsd };
}
